// The desk's Undo toasts (UI-audit a3-13): the same `Toast` with the same
// Undo the phone offers for the same actions, so one click on a desk × or
// Delete or Commit can be taken back exactly as a swipe can. Each takes the
// mutation the screen already holds rather than holding its own, so the
// write goes through the same optimistic patch and the same error banner.

import { toast } from '../ui/Toast'
import { supabase } from '../lib/supabase'
import { queryClient } from '../lib/store'
import type { ListItem } from '../lib/types'

const LIST_ITEMS = ['list_items'] as const

/** One line back with every column it had, whatever its list's kind — the
 *  phone's per-kind `restore`s each copy their own kind's columns, and the
 *  desk draws every kind with one `Line`. Returns the new row's id. */
async function insertLine(i: ListItem, parent_id: string | null): Promise<string | null> {
  const { data, error } = await supabase.from('list_items').insert({
    list_id: i.list_id, parent_id, body: i.body, link: i.link, section: i.section, position: i.position,
    done_at: i.done_at, qty: i.qty, due_on: i.due_on, price_cents: i.price_cents, currency: i.currency,
    status: i.status, image_url: i.image_url, flagged_at: i.flagged_at, note: i.note, tags: i.tags,
    entry_id: i.entry_id, repeat_every: i.repeat_every ?? null, repeat_unit: i.repeat_unit ?? null,
  }).select('id').single()
  return error || !data ? null : (data as { id: string }).id
}

/** "Removed" with Undo. `children` are the subtasks Postgres deletes with the
 *  line (`parent_id … on delete cascade`): Undo writes them back under the
 *  restored line's new id, so a task comes back with what was under it. */
export function toastRemovedLine(line: ListItem, children: ListItem[], remove: (id: string) => void) {
  remove(line.id)
  toast({
    text: `Removed "${line.body}"`,
    action: {
      label: 'Undo',
      onClick: () => void (async () => {
        const id = await insertLine(line, line.parent_id)
        if (id) for (const c of children) await insertLine(c, id)
        void queryClient.invalidateQueries({ queryKey: LIST_ITEMS })
      })(),
    },
  })
}

/** A commitment just written, with Undo. The id is read off the insert's own
 *  response (`scheduleEntry` selects it), as the phone's `EventActions` does;
 *  with no id there is nothing to take back, so no button. */
export function toastCommitted(text: string, res: unknown, remove: (id: string) => void) {
  const id = (res as { data?: { id?: string } | null } | null)?.data?.id ?? null
  toast({ text, action: id ? { label: 'Undo', onClick: () => remove(id) } : undefined })
}

/** "Added to <list>" with Undo. The line is found at click time, after a
 *  refetch: the optimistic row in the cache carries a temp id until the
 *  insert's refetch lands, and deleting a temp id deletes nothing. */
export function toastAddedToList(name: string, listId: string, entryId: string, remove: (id: string) => void) {
  toast({
    text: `Added to ${name}`,
    action: {
      label: 'Undo',
      onClick: () => void (async () => {
        await queryClient.refetchQueries({ queryKey: LIST_ITEMS })
        const it = (queryClient.getQueryData<ListItem[]>(LIST_ITEMS) ?? [])
          .find(i => i.list_id === listId && i.entry_id === entryId)
        if (it) remove(it.id)
      })(),
    },
  })
}
